import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import '../../Style/EditModal.scss';
import "../../Style/TableStyle.scss"
import '../../Style/__searchbar.scss'
import '../../Style/__button.scss'
import './KartelaArtikullitPage.scss';
import "../AccountingReportsCRUD/AccountsReportPage.scss";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { FaChartLine } from "react-icons/fa";
import "jspdf-autotable";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { errorAlert } from "../../Components/Alerts";
import { AddActionsButton } from "../../Components/AddElementsButton";
import { AiOutlineClose } from "react-icons/ai";
import ExcelFormat from "../../Components/ExcelConverter/ExcelFormat";
import { ArticleCardGridHeader, ArticleDetailsColumns, ArticleDetailsGrid, ArticleDetailsMappings, sinteticArticleColumns, sinteticArticleMappings } from "./GridHeaders/ArticleCardGrid";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";

const ARTICLE_CARD_ENDPOINTS = {
    syntheticV2: 'Card/articleSyntheticAnalysisV2',
    detailsV2: 'Card/articleDetailsV2',
    detailsChartV2: 'Card/articleDetailsChartV2',
    syntheticLegacy: 'Card/articleSyntheticAnalysis',
    detailsLegacy: 'Card/articleDetails',
    detailsChartLegacy: 'Card/articleDetailsChart',
};

export default function KartelaArtikullitPage() {
    const [data, setData] = useState([]);
    const [mainLoading, setMainLoading] = useState(false);
    const [mainRowCount, setMainRowCount] = useState(0);
    const [mainTotals, setMainTotals] = useState({});
    const headers = authHeader();
    const { fiscalYear } = useContext(FiscalYearContext);
    const [articleDetails, setArticleDetails] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsRowCount, setDetailsRowCount] = useState(0);
    const [detailsTotals, setDetailsTotals] = useState({});
    const [selectedArticleId, setSelectedArticleId] = useState(null);

    const [showallAccounts, setShowAllAccounts] = useState(true);
    const [showDetailAccounts, setShowDetailsAccounts] = useState(false);

    const [articleName, setArticleName] = useState("");

    const [articleDetailsChart, setArticleDetailsChart] = useState([]);
    const [mainPaginationModel, setMainPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [mainSortModel, setMainSortModel] = useState([]);
    const [mainFilterModel, setMainFilterModel] = useState({ items: [] });
    const [mainQuickFilter, setMainQuickFilter] = useState([]);
    const [detailsPaginationModel, setDetailsPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [detailsSortModel, setDetailsSortModel] = useState([]);
    const [detailsFilterModel, setDetailsFilterModel] = useState({ items: [] });
    const [detailsQuickFilter, setDetailsQuickFilter] = useState([]);

    const requestWithFallback = async (v2Endpoint, legacyEndpoint, params) => {
        try {
            return await axios.get(ApiLink.API_URL + v2Endpoint, { headers, params });
        } catch (error) {
            if (error?.response?.status !== 404) {
                throw error;
            }

            return axios.get(ApiLink.API_URL + legacyEndpoint, { headers, params });
        }
    };

    const buildGridParams = (paginationModel, sortModel, filterModel, quickFilter, extra = {}) => ({
        fiscalYear,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sort: JSON.stringify(sortModel || []),
        filter: JSON.stringify(filterModel?.items || []),
        quickFilter: (quickFilter || []).join(" "),
        ...extra,
    });

    const normalizePagedPayload = (payload) => {
        if (Array.isArray(payload)) {
            return {
                items: payload,
                total: payload.length,
                totals: null,
            };
        }

        return {
            items: Array.isArray(payload?.items) ? payload.items : Array.isArray(payload?.Items) ? payload.Items : [],
            total: Number(payload?.total ?? payload?.Total ?? 0),
            totals: payload?.totals || payload?.Totals || null,
        };
    };

    const fetchMaterialSintetikData = async () => {
        setMainLoading(true);
        try {
            const response = await requestWithFallback(
                ARTICLE_CARD_ENDPOINTS.syntheticV2,
                ARTICLE_CARD_ENDPOINTS.syntheticLegacy,
                buildGridParams(mainPaginationModel, mainSortModel, mainFilterModel, mainQuickFilter)
            );

            const normalized = normalizePagedPayload(response.data);
            setData(normalized.items);
            setMainRowCount(Number(normalized.total ?? normalized.items.length));
            setMainTotals(normalized.totals || {});
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë.");
            }
        } finally {
            setMainLoading(false);
        }
    };

    const rows = useMemo(() => data.map((item, index) => ({
        id: index + 1,
        rowNumber: mainPaginationModel.page * mainPaginationModel.pageSize + index + 1,
        barcode: item.Barcode,
        articleName: item.ArticleName,
        jan: item.Janar,
        feb: item.Shkurt,
        mar: item.Mars,
        apr: item.Prill,
        may: item.Maj,
        jun: item.Qershor,
        jul: item.Korrik,
        aug: item.Gusht,
        sep: item.Shtator,
        oct: item.Tetor,
        nov: item.Nentor,
        dec: item.Dhjetor,
        tot: item.Total,
        avg: item.CmimiMesatar,
        valEu: item.VleraNeEuro,

        articleID: item.ArticleID,
        rowInfo: item,
    })), [data, mainPaginationModel.page, mainPaginationModel.pageSize]);

    const detailsRows = useMemo(() => articleDetails.map((item, index) => ({
        id: index + 1,
        rowNumber: detailsPaginationModel.page * detailsPaginationModel.pageSize + index + 1,
        barcode: item.Barcode,
        articleName: item.ArticleName,
        accountName: item.AccountName,
        jan: item.Janar,
        feb: item.Shkurt,
        mar: item.Mars,
        apr: item.Prill,
        may: item.Maj,
        jun: item.Qershor,
        jul: item.Korrik,
        aug: item.Gusht,
        sep: item.Shtator,
        oct: item.Tetor,
        nov: item.Nentor,
        dec: item.Dhjetor,
        tot: item.Total,
        avg: item.CmimiMesatar,
        valEu: item.VleraNeEuro,
    })), [articleDetails, detailsPaginationModel.page, detailsPaginationModel.pageSize]);

    const currencyFormatter = useMemo(() => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }), []);

    const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

    const syntheticColumns = useMemo(() => {
        const columnWidthByField = ArticleCardGridHeader.reduce((accumulator, column) => {
            accumulator[column.field] = Number(column.width || column.minWidth || 130);
            return accumulator;
        }, {});

        return [
            { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
            { field: "barcode", headerName: "Barkodi", minWidth: 130, width: columnWidthByField.barcode || 150 },
            { field: "articleName", headerName: "Emertimi artikullit", minWidth: 180, width: columnWidthByField.articleName || 180 },
            { field: "jan", headerName: "Janar", minWidth: 120, width: columnWidthByField.jan || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "feb", headerName: "Shkurt", minWidth: 120, width: columnWidthByField.feb || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "mar", headerName: "Mars", minWidth: 120, width: columnWidthByField.mar || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "apr", headerName: "Prill", minWidth: 120, width: columnWidthByField.apr || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "may", headerName: "Maj", minWidth: 120, width: columnWidthByField.may || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "jun", headerName: "Qershor", minWidth: 120, width: columnWidthByField.jun || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "jul", headerName: "Korrik", minWidth: 120, width: columnWidthByField.jul || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "aug", headerName: "Gusht", minWidth: 120, width: columnWidthByField.aug || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "sep", headerName: "Shtator", minWidth: 120, width: columnWidthByField.sep || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "oct", headerName: "Tetor", minWidth: 120, width: columnWidthByField.oct || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "nov", headerName: "Nentor", minWidth: 120, width: columnWidthByField.nov || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "dec", headerName: "Dhjetor", minWidth: 120, width: columnWidthByField.dec || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "tot", headerName: "Total", minWidth: 130, width: columnWidthByField.tot || 130, valueFormatter: (value) => formatCurrency(value) },
            { field: "avg", headerName: "Cmimi mesatar", minWidth: 140, width: columnWidthByField.avg || 140, valueFormatter: (value) => formatCurrency(value) },
            { field: "valEu", headerName: "Vlera ne euro", minWidth: 140, width: columnWidthByField.valEu || 140, valueFormatter: (value) => formatCurrency(value) },
        ];
    }, [currencyFormatter]);

    const detailColumns = useMemo(() => {
        const columnWidthByField = ArticleDetailsGrid.reduce((accumulator, column) => {
            accumulator[column.field] = Number(column.width || column.minWidth || 130);
            return accumulator;
        }, {});

        return [
            { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
            { field: "barcode", headerName: "Barkodi", minWidth: 130, width: columnWidthByField.barcode || 150 },
            { field: "articleName", headerName: "Emertimi artikullit", minWidth: 180, width: columnWidthByField.articleName || 180 },
            { field: "accountName", headerName: "Klienti", minWidth: 170, width: columnWidthByField.accountName || 170 },
            { field: "jan", headerName: "Janar", minWidth: 120, width: columnWidthByField.jan || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "feb", headerName: "Shkurt", minWidth: 120, width: columnWidthByField.feb || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "mar", headerName: "Mars", minWidth: 120, width: columnWidthByField.mar || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "apr", headerName: "Prill", minWidth: 120, width: columnWidthByField.apr || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "may", headerName: "Maj", minWidth: 120, width: columnWidthByField.may || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "jun", headerName: "Qershor", minWidth: 120, width: columnWidthByField.jun || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "jul", headerName: "Korrik", minWidth: 120, width: columnWidthByField.jul || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "aug", headerName: "Gusht", minWidth: 120, width: columnWidthByField.aug || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "sep", headerName: "Shtator", minWidth: 120, width: columnWidthByField.sep || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "oct", headerName: "Tetor", minWidth: 120, width: columnWidthByField.oct || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "nov", headerName: "Nentor", minWidth: 120, width: columnWidthByField.nov || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "dec", headerName: "Dhjetor", minWidth: 120, width: columnWidthByField.dec || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "tot", headerName: "Total", minWidth: 130, width: columnWidthByField.tot || 130, valueFormatter: (value) => formatCurrency(value) },
            { field: "avg", headerName: "Cmimi mesatar", minWidth: 140, width: columnWidthByField.avg || 140, valueFormatter: (value) => formatCurrency(value) },
            { field: "valEu", headerName: "Vlera ne euro", minWidth: 140, width: columnWidthByField.valEu || 140, valueFormatter: (value) => formatCurrency(value) },
        ];
    }, [currencyFormatter]);

    const summaryFooter = useMemo(() => ({
        labelField: "articleName",
        label: "Totali",
        values: {
            jan: formatCurrency(mainTotals?.janar ?? mainTotals?.Janar ?? 0),
            feb: formatCurrency(mainTotals?.shkurt ?? mainTotals?.Shkurt ?? 0),
            mar: formatCurrency(mainTotals?.mars ?? mainTotals?.Mars ?? 0),
            apr: formatCurrency(mainTotals?.prill ?? mainTotals?.Prill ?? 0),
            may: formatCurrency(mainTotals?.maj ?? mainTotals?.Maj ?? 0),
            jun: formatCurrency(mainTotals?.qershor ?? mainTotals?.Qershor ?? 0),
            jul: formatCurrency(mainTotals?.korrik ?? mainTotals?.Korrik ?? 0),
            aug: formatCurrency(mainTotals?.gusht ?? mainTotals?.Gusht ?? 0),
            sep: formatCurrency(mainTotals?.shtator ?? mainTotals?.Shtator ?? 0),
            oct: formatCurrency(mainTotals?.tetor ?? mainTotals?.Tetor ?? 0),
            nov: formatCurrency(mainTotals?.nentor ?? mainTotals?.Nentor ?? 0),
            dec: formatCurrency(mainTotals?.dhjetor ?? mainTotals?.Dhjetor ?? 0),
            tot: formatCurrency(mainTotals?.total ?? mainTotals?.Total ?? 0),
            avg: "",
            valEu: formatCurrency(mainTotals?.vleraNeEuro ?? mainTotals?.VleraNeEuro ?? 0),
        },
    }), [mainTotals]);

    const detailsSummaryFooter = useMemo(() => ({
        labelField: "articleName",
        label: "Totali",
        values: {
            jan: formatCurrency(detailsTotals?.janar ?? detailsTotals?.Janar ?? 0),
            feb: formatCurrency(detailsTotals?.shkurt ?? detailsTotals?.Shkurt ?? 0),
            mar: formatCurrency(detailsTotals?.mars ?? detailsTotals?.Mars ?? 0),
            apr: formatCurrency(detailsTotals?.prill ?? detailsTotals?.Prill ?? 0),
            may: formatCurrency(detailsTotals?.maj ?? detailsTotals?.Maj ?? 0),
            jun: formatCurrency(detailsTotals?.qershor ?? detailsTotals?.Qershor ?? 0),
            jul: formatCurrency(detailsTotals?.korrik ?? detailsTotals?.Korrik ?? 0),
            aug: formatCurrency(detailsTotals?.gusht ?? detailsTotals?.Gusht ?? 0),
            sep: formatCurrency(detailsTotals?.shtator ?? detailsTotals?.Shtator ?? 0),
            oct: formatCurrency(detailsTotals?.tetor ?? detailsTotals?.Tetor ?? 0),
            nov: formatCurrency(detailsTotals?.nentor ?? detailsTotals?.Nentor ?? 0),
            dec: formatCurrency(detailsTotals?.dhjetor ?? detailsTotals?.Dhjetor ?? 0),
            tot: formatCurrency(detailsTotals?.total ?? detailsTotals?.Total ?? 0),
            avg: "",
            valEu: formatCurrency(detailsTotals?.vleraNeEuro ?? detailsTotals?.VleraNeEuro ?? 0),
        },
    }), [detailsTotals]);

    const monthNames = [
        "Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor",
        "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"
    ];

    function getMonthName(monthNumber) {
        return monthNames[monthNumber - 1] || "";
    }

    const monthlyChartRows = useMemo(() => {
        return (articleDetailsChart || []).map((item) => ({
            ...item,
            monthLabel: getMonthName(item.MonthNumber),
        }));
    }, [articleDetailsChart]);

    const fetchArticleDetails = async (idNumber, fiscalY) => {
        setDetailsLoading(true);
        try {
            const response = await requestWithFallback(
                ARTICLE_CARD_ENDPOINTS.detailsV2,
                ARTICLE_CARD_ENDPOINTS.detailsLegacy,
                buildGridParams(
                    detailsPaginationModel,
                    detailsSortModel,
                    detailsFilterModel,
                    detailsQuickFilter,
                    { articleID: idNumber, fiscalYear: fiscalY }
                )
            );

            const normalized = normalizePagedPayload(response.data);
            setArticleDetails(normalized.items);
            setDetailsRowCount(Number(normalized.total ?? normalized.items.length));
            setDetailsTotals(normalized.totals || {});
            setArticleName(normalized.items.length > 0 ? normalized.items[0].ArticleName : "");
            fetchArticleDetailsForChart(idNumber, fiscalY);
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
            if (error.response && error.response.data && error.response.data.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        } finally {
            setDetailsLoading(false);
        }
    };

    const fetchArticleDetailsForChart = async (idNumber, fiscalY) => {
        try {
            const response = await requestWithFallback(
                ARTICLE_CARD_ENDPOINTS.detailsChartV2,
                ARTICLE_CARD_ENDPOINTS.detailsChartLegacy,
                { articleID: idNumber, fiscalYear: fiscalY }
            );

            setArticleDetailsChart(Array.isArray(response.data?.MonthlyData) ? response.data.MonthlyData : []);
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
            if (error.response && error.response.data && error.response.data.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        }
    };
    const showDetails = () => {
        setShowAllAccounts(false);
        setShowDetailsAccounts(true);
    }

    const showAllAccounts = () => {
        setShowAllAccounts(true);
        setShowDetailsAccounts(false);
        setArticleDetails([]);
        setArticleDetailsChart([]);
        setArticleName("");
        setSelectedArticleId(null);
        setDetailsRowCount(0);
        setDetailsTotals({});
    }

    useEffect(() => {
        fetchMaterialSintetikData();
    }, [fiscalYear, mainPaginationModel, mainSortModel, mainFilterModel, mainQuickFilter]);

    useEffect(() => {
        if (!selectedArticleId) {
            return;
        }

        fetchArticleDetails(selectedArticleId, fiscalYear);
    }, [selectedArticleId, fiscalYear, detailsPaginationModel, detailsSortModel, detailsFilterModel, detailsQuickFilter]);

    const openArticleDetails = (row) => {
        const articleId = row?.articleID || row?.ArticleID;
        if (!articleId) {
            return;
        }

        setDetailsPaginationModel({ page: 0, pageSize: detailsPaginationModel.pageSize || 50 });
        setDetailsSortModel([]);
        setDetailsFilterModel({ items: [] });
        setDetailsQuickFilter([]);
        setSelectedArticleId(articleId);
        showDetails();
    };

    return (
        <div className="Crud__Container ARP__page KAP__page">
            {showallAccounts && (
                <div className="ARP__content">
                    <CrudPageHeader
                        icon={FaChartLine}
                        title="Kartela sintetike e artikujve"
                        description="Forma është e përshtatur për kontroll, filtrim dhe analizë profesionale të lëvizjeve mujore sipas artikullit në vitin fiskal aktiv."
                        className="ARP__header"
                    />

                    <div className="ARP__reportGrid">
                        <ReusableAccountBooksGrid
                            columns={syntheticColumns}
                            rows={rows}
                            loading={mainLoading}
                            rowCount={mainRowCount}
                            height={650}
                            paginationModel={mainPaginationModel}
                            setPaginationModel={setMainPaginationModel}
                            sortModel={mainSortModel}
                            setSortModel={setMainSortModel}
                            filterModel={mainFilterModel}
                            setFilterModel={setMainFilterModel}
                            quickFilter={mainQuickFilter}
                            setQuickFilter={setMainQuickFilter}
                            showViewAction
                            showEditAction={false}
                            showDeleteAction={false}
                            showExportButton={false}
                            summaryFooter={summaryFooter}
                            onView={openArticleDetails}
                            toolbarActions={(
                                <ExcelFormat
                                    data={data}
                                    fileName={`Kartela sintetike e artikujve`}
                                    columns={sinteticArticleColumns}
                                    columnMappings={sinteticArticleMappings}
                                    buttonText="XLSX"
                                />
                            )}
                        />
                    </div>
                </div>
            )}

            {showDetailAccounts && (
                <div className="ARP__content">
                    <CrudPageHeader
                        icon={FaChartLine}
                        title={`Historiku i artikullit: ${articleName}`}
                        description="Forma është e përshtatur për kontroll, filtrim dhe analizë profesionale të detajeve sipas klientit dhe trendit mujor."
                        className="ARP__header"
                        action={(
                            <AddActionsButton
                                text="Mbyll"
                                onClick={showAllAccounts}
                                icon={<AiOutlineClose />}
                                className="RSDG__toolButton ARP__backBtn"
                            />
                        )}
                    />

                    <div className="KAP__chartCard ARP__reportGrid">
                        <div className="KAP__chartHead">
                            <h4>Trend mujor i artikullit</h4>
                            <p>Sasia dhe vlera mujore sipas vitit fiskal aktiv.</p>
                        </div>

                        <ResponsiveContainer width="100%" height={500}>
                            <BarChart
                                width={500}
                                height={500}
                                data={monthlyChartRows}

                            >
                                <CartesianGrid strokeDasharray="5 5" />
                                <XAxis dataKey="monthLabel" />
                                <YAxis />
                                <Tooltip
                                    labelFormatter={(label) => `Muaji: ${label}`}
                                />
                                <Legend />
                                <Bar dataKey="MonthQty" fill="#284945" name="Sasia" barSize={15} />
                                <Bar dataKey="MonthValue" fill="#82ca9d" name="Vlera" barSize={15} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="ARP__reportGrid ARP__reportGrid--details">
                        <ReusableAccountBooksGrid
                            columns={detailColumns}
                            rows={detailsRows}
                            loading={detailsLoading}
                            rowCount={detailsRowCount}
                            height={650}
                            paginationModel={detailsPaginationModel}
                            setPaginationModel={setDetailsPaginationModel}
                            sortModel={detailsSortModel}
                            setSortModel={setDetailsSortModel}
                            filterModel={detailsFilterModel}
                            setFilterModel={setDetailsFilterModel}
                            quickFilter={detailsQuickFilter}
                            setQuickFilter={setDetailsQuickFilter}
                            showViewAction={false}
                            showEditAction={false}
                            showDeleteAction={false}
                            showExportButton={false}
                            summaryFooter={detailsSummaryFooter}
                            toolbarActions={(
                                <ExcelFormat
                                    data={articleDetails}
                                    fileName={`${articleName}-Detale`}
                                    columns={ArticleDetailsColumns}
                                    columnMappings={ArticleDetailsMappings}
                                    buttonText="XLSX"
                                />
                            )}
                        />
                    </div>
                </div>

            )}
        </div>
    )
}